import { DatePipe } from '@angular/common';
import { Component, computed, inject, input, OnDestroy } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideCopy, lucideLink2, lucideLink2Off, lucidePen } from '@ng-icons/lucide';
import { HlmButton } from '@spartan-ng/helm/button';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';

import { ToastrSuccessComponent } from '../../../shared/components/toastr/toastr-success.component';
import { Sequence } from '../../models/séquence.model';
import { ÉtatActivité } from '../../models/état-activité.enum';
import { ActivitéStore } from '../../store/activité.store';
import { SequenceStore } from '../../store/sequence.store';
import { Référentiel } from '../../../administration/gestion-référentiels/models/référentiel.model';

@Component({
  selector: 'app-activite-description',
  imports: [NgIcon, HlmButton, DatePipe, RouterModule],
  templateUrl: './activite-description.component.html',
  providers: [provideIcons({ lucideLink2, lucideLink2Off, lucideCopy, lucidePen })],
})
export class ActiviteDescriptionComponent implements OnDestroy {
  readonly #activiteStore = inject(ActivitéStore);
  readonly #sequenceStore = inject(SequenceStore);
  readonly #router = inject(Router);

  readonly activite = this.#activiteStore.selectedEntity;
  readonly sequence = input.required<Sequence>();
  readonly referentiel = input.required<Référentiel>();

  etatActivite = ÉtatActivité;

  estAssocie = computed(() => {
    const activite = this.activite();

    if (!activite) return false;

    return this.sequence()
      .activites?.map((a) => a.id)
      .includes(activite.id);
  });

  ngOnDestroy(): void {
    toast.dismiss();
  }

  toggleSequence() {
    const activite = this.activite();
    const sequenceSelected = this.sequence();

    if (!activite) return;

    const toastrId = uuidv7();

    let activites = sequenceSelected.activites ?? [];

    if (!this.estAssocie()) {
      activites.push(activite);
    } else {
      activites = activites.filter((a) => a.id !== activite.id);
    }

    if (sequenceSelected) {
      this.#sequenceStore.modifier({
        id: sequenceSelected.id,
        data: {
          nom: sequenceSelected.nom,
          indice: sequenceSelected.indice,
          type: sequenceSelected.type,
          activites: activites,
        },
        next: () => {
          toast.custom(ToastrSuccessComponent, {
            componentProps: {
              message: `L'activité ${activite.nom} a bien été ${this.estAssocie() ? 'déliée' : 'liée'} à la séquence ${sequenceSelected.nom ?? sequenceSelected.codeSequence}`,
              id: toastrId,
            },
            id: toastrId,
          });
        },
      });
    }
  }

  dupliquer() {
    const activite = this.activite();
    const sequence = this.sequence();

    if (activite && sequence) {
      const idActiviteParente = activite.idActiviteParente ?? activite.id;
      this.#activiteStore.dupliquer({
        data: {
          idActiviteParente: idActiviteParente,
          idReferentiel: this.referentiel().id,
          idSequence: sequence.id,
        },
        next: (result) => {
          let segments = this.#router.url.split('/');
          segments = segments.slice(0, 11);
          segments.push('édition', result.id);
          this.#router.navigateByUrl(segments.join('/'));
        },
      });
    }
  }
}
